/**
 * Shared Unify email layout and branding resolver.
 *
 * Browser-safe: no `@medplum/core`, no Node built-ins. Imported by medplum-ui
 * (template rendering, preview) and medplum-server (bot emails). This is the one
 * place email chrome is styled; callers pass branding parameters, never colors.
 *
 * Tables and inline styles only (mail clients ignore <style>/var()). No logo and
 * no <img>: the header is the sender name on the accent bar.
 *
 * Token comments name the `medplum-ui/src/config/theme.ts` scale entry they mirror.
 */

/** Attribute name; present once in every wrapped document; wrapping already-wrapped HTML is a no-op. */
export const EMAIL_LAYOUT_MARKER = 'data-unify-email-layout';

const EMAIL_LAYOUT_MARKER_ATTRIBUTE = `${EMAIL_LAYOUT_MARKER}="1"`;


export interface EmailBranding {
  senderName: string;
  supportEmail: string;
  /** Header bar background (the sender name on it is dark or white, whichever has more contrast). */
  accentColor: string;
  pageBackground: string;
  /** Optional line shown in the footer under the sender name. */
  footerText?: string;
}

export const DEFAULT_EMAIL_BRANDING: Readonly<EmailBranding> = {
  senderName: 'Unify Mental Health',
  supportEmail: 'support@unifymentalhealth.com',
  accentColor: '#C9A961', // config/theme.ts gold[5]
  pageBackground: '#F8F6F1', // config/theme.ts gold[0]
};

const CARD_BORDER = '#EDE6D4'; // config/theme.ts gold[1]
const HEADING_COLOR = '#1F2937'; // config/theme.ts clinicalGray[9]
const BODY_COLOR = '#374151'; // config/theme.ts clinicalGray[8]
const SECONDARY_COLOR = '#6B7280'; // config/theme.ts clinicalGray[6]
const DIVIDER_COLOR = '#E5E7EB'; // config/theme.ts clinicalGray[3]
const CARD_BACKGROUND = '#FFFFFF';
const RADIUS = '8px';
const FONT = 'font-family:Arial,Helvetica,sans-serif;';


/** Opening tags, quote-aware (a `>` inside a quoted attribute value does not end the tag); bounded, so linear. */
const OPEN_TAG_PATTERN = /<[a-zA-Z](?:"[^"]*"|'[^']*'|[^'">]){0,4000}>/g;
/** One CSS declaration: separator, property, value. */
const DECLARATION_PATTERN = /(^|;)(\s*)([\w-]+)(\s*:)([^;]*)/g;
const COLOR_BEARING_PROPERTY =
  /^(?:background(?:-color|-image)?|border(?:-(?:top|right|bottom|left))?(?:-color)?|outline(?:-color)?|box-shadow|text-decoration-color|color)$/i;
/** One attribute (name, optional value) inside an opening tag; quoted values are consumed whole. */
const ATTRIBUTE_PATTERN = /(\s)([^\s=<>"'/]+)(?:(\s*=\s*)("[^"]*"|'[^']*'|[^\s"'<>]+))?/g;
const DEFAULT_ACCENT = /#C9A961/gi;

/**
 * Re-point the default accent in color-bearing declarations. Backgrounds and borders take the accent itself; the `color`
 * property (text) takes `textAccent`, which is only the accent when it is readable on white.
 */
function swapAccentInStyle(style: string, accent: string, textAccent: string): string {
  return style.replace(DECLARATION_PATTERN, (all, sep: string, ws: string, prop: string, colon: string, value: string) => {
    if (!COLOR_BEARING_PROPERTY.test(prop)) return all;
    const target = prop.toLowerCase() === 'color' ? textAccent : accent;
    return `${sep}${ws}${prop}${colon}${value.replace(DEFAULT_ACCENT, target)}`;
  });
}
/** The layout marker, only as an attribute of an opening `<body>` tag. */
const BODY_MARKER_PATTERN = new RegExp(`<body\\b[^<>]*\\s${EMAIL_LAYOUT_MARKER}\\s*=\\s*["']?1`, 'i');
const DARK_TEXT = '#1F2937';
const LIGHT_TEXT = '#FFFFFF';
const BUTTON_BACKGROUND = HEADING_COLOR;
const SAFE_HREF_RE = /^(?:https?:\/\/|mailto:)[^\s"'<>]+$/i;
const EMAIL_ADDRESS_RE = /^[^\s@<>"'?#,;]+@[^\s@<>"'?#,;]+\.[^\s@<>"'?#,;]+$/;
const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

function validHex(value: string | undefined, fallback: string): string {
  return value !== undefined && HEX_COLOR_RE.test(value) ? value : fallback;
}

function validEmail(value: string | undefined, fallback: string): string {
  return value !== undefined && EMAIL_ADDRESS_RE.test(value) ? value : fallback;
}

function nonEmpty(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
}

/** True when the HTML is already a wrapped document (marker attribute on its opening `<body>` tag). */
export function hasEmailLayoutMarker(html: string): boolean {
  return BODY_MARKER_PATTERN.test(html);
}

function channelLuminance(hex: string, start: number): number {
  const c = parseInt(hex.slice(start, start + 2), 16) / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of a 6-digit hex color. */
export function relativeLuminance(hex: string): number {
  return 0.2126 * channelLuminance(hex, 1) + 0.7152 * channelLuminance(hex, 3) + 0.0722 * channelLuminance(hex, 5);
}

/** WCAG contrast ratio between two 6-digit hex colors. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Accent when it is readable as text on `background` (WCAG AA, 4.5:1), otherwise dark text. */
export function readableLinkColor(accent: string, background: string = CARD_BACKGROUND): string {
  return contrastRatio(accent, background) >= 4.5 ? accent : DARK_TEXT;
}

/** Dark (#1F2937) or white text, whichever has the higher contrast on `background`. */
export function contrastTextColor(background: string): string {
  return contrastRatio(background, DARK_TEXT) >= contrastRatio(background, LIGHT_TEXT) ? DARK_TEXT : LIGHT_TEXT;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Defaults with `overrides` applied per field when valid (6-digit hex colors only).
 * Practice brand-color extensions were retired in P5-A; the accent is never read from an Organization.
 */
export function resolveEmailBranding(overrides?: Partial<EmailBranding>): EmailBranding {
  const d = DEFAULT_EMAIL_BRANDING;
  const o = overrides ?? {};
  const footerText = o.footerText?.trim();
  return {
    senderName: nonEmpty(o.senderName, d.senderName),
    supportEmail: validEmail(o.supportEmail?.trim(), d.supportEmail),
    accentColor: validHex(o.accentColor, d.accentColor),
    pageBackground: validHex(o.pageBackground, d.pageBackground),
    ...(footerText ? { footerText } : {}),
  };
}

/** Call-to-action button (table-based for Outlook): always dark with white text, whatever the accent. */
export function buildEmailButton(params: { href: string; label: string }): string {
  let href = '#';
  if (SAFE_HREF_RE.test(params.href.trim())) {
    href = params.href.trim();
  } else {
    // Never log the value: it may carry tokens.
    console.warn('[email-layout] buildEmailButton rejected an href with a disallowed scheme or characters');
  }
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin:0;"><tr><td bgcolor="${BUTTON_BACKGROUND}" style="background-color:${BUTTON_BACKGROUND};border-radius:6px;mso-padding-alt:12px 28px;"><a data-unify-button="1" href="${escapeHtml(href)}" style="${FONT}display:inline-block;padding:12px 28px;mso-padding-alt:12px 28px;font-size:15px;line-height:22px;font-weight:600;color:${LIGHT_TEXT};text-decoration:none;">${escapeHtml(params.label)}</a></td></tr></table>`;
}

/**
 * Wrap a body fragment in the Unify email document. Returns `bodyHtml` unchanged
 * when it already carries {@link EMAIL_LAYOUT_MARKER}. In the fragment, the default
 * accent hex is re-pointed to the resolved accent so fragment buttons follow an
 * accent override.
 */
export function buildEmailLayout(params: {
  bodyHtml: string;
  branding?: EmailBranding;
  /** Document title (default: sender name). */
  title?: string;
  /** Hidden inbox-preview text. */
  preheader?: string;
}): string {
  if (hasEmailLayoutMarker(params.bodyHtml)) return params.bodyHtml;
  const b = params.branding ?? DEFAULT_EMAIL_BRANDING;
  const d = DEFAULT_EMAIL_BRANDING;
  const accent = validHex(b.accentColor, d.accentColor);
  const onAccent = contrastTextColor(accent);
  const page = validHex(b.pageBackground, d.pageBackground);
  const sender = escapeHtml(nonEmpty(b.senderName, d.senderName));
  const support = escapeHtml(validEmail(b.supportEmail?.trim(), d.supportEmail));
  const footerText = b.footerText?.trim()
    ? `<p style="${FONT}font-size:12px;line-height:18px;color:${SECONDARY_COLOR};margin:8px 0 0;text-align:center;">${escapeHtml(b.footerText.trim())}</p>`
    : '';
  const swapAccent = accent.toUpperCase() !== d.accentColor;
  const textAccent = readableLinkColor(accent);
  const body =
    swapAccent
      ? params.bodyHtml.replace(OPEN_TAG_PATTERN, (tag) => {
          return tag.replace(ATTRIBUTE_PATTERN, (all, ws: string, name: string, eq: string | undefined, value: string | undefined) => {
            if (value === undefined) return all;
            const lowerName = name.toLowerCase();
            let next = value;
            if (lowerName === 'style') {
              const q = value[0] === '"' || value[0] === "'" ? value[0] : '';
              let css = q ? value.slice(1, -1) : value;
              if (swapAccent) css = swapAccentInStyle(css, accent, textAccent);
              next = `${q}${css}${q}`;
            } else if (swapAccent && (lowerName === 'bgcolor' || lowerName === 'color')) {
              next = next.replace(DEFAULT_ACCENT, lowerName === 'color' ? textAccent : accent);
            }
            return `${ws}${name}${eq}${next}`;
          });
        })
      : params.bodyHtml;
  const title = escapeHtml(nonEmpty(params.title, nonEmpty(b.senderName, d.senderName)));
  const preheader = params.preheader?.trim()
    ? `<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;">${escapeHtml(params.preheader.trim())}</div>\n`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="color-scheme" content="light"><meta name="supported-color-schemes" content="light"><title>${title}</title></head>
<body ${EMAIL_LAYOUT_MARKER_ATTRIBUTE} style="margin:0;padding:0;background-color:${page};${FONT}font-size:15px;line-height:22px;color:${BODY_COLOR};">
${preheader}<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;background-color:${page};">
<tr><td align="center" style="padding:40px 16px;">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;width:100%;max-width:600px;background-color:${CARD_BACKGROUND};border:1px solid ${CARD_BORDER};border-radius:${RADIUS};">
<tr><td style="background-color:${accent};padding:24px 32px;border-radius:${RADIUS} ${RADIUS} 0 0;"><span style="${FONT}font-size:20px;line-height:28px;font-weight:600;color:${onAccent};">${sender}</span></td></tr>
<tr><td style="padding:32px;${FONT}font-size:15px;line-height:22px;color:${BODY_COLOR};">${body}</td></tr>
<tr><td style="padding:20px 32px;border-top:1px solid ${DIVIDER_COLOR};">
<p style="${FONT}font-size:12px;line-height:18px;color:${SECONDARY_COLOR};margin:0;text-align:center;"><strong style="color:${HEADING_COLOR};">${sender}</strong> &middot; <a href="mailto:${support}" style="color:${SECONDARY_COLOR};">${support}</a></p>${footerText}
</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}
